import { Stack, router } from 'expo-router';
import * as React from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import {
  Button,
  IconDelivery,
  IconLocationFill,
  IconStore,
  ScrollView,
  Text,
  createMqStyles,
  tokens,
} from '@fhs/ui';
import { Box } from '@fhs-legacy/universal-components';
import { ILocation } from '@rbi-ctg/frontend';
import { useGetNearbyRestaurantsFromDelivery } from 'hooks/use-get-nearby-restaurants-from-delivery-context';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import { ServiceMode } from 'state/service-mode';
import { convertMilesToMeters } from 'utils/distance';

import {
  webFocusableSectionTextProps,
  webNamedRegion,
  webSectionHeadingWebProps,
} from '../../../utils/web-named-region';

import { AddressBox } from './address-box';
import { ButtonContainer, Container } from './delivery-unavailable.styled';
import { IDeliveryUnavailableErrorProps, IDeliveryUnavailableProps } from './types';

const searchRadiusInMiles = 20;

const DELIVERY_UNAVAILABLE_TITLE_ID = 'delivery-unavailable-title';
const DELIVERY_UNAVAILABLE_YOUR_ADDRESS_HEADING_ID = 'delivery-unavailable-your-address-heading';
const DELIVERY_UNAVAILABLE_PICKUP_HEADING_ID = 'delivery-unavailable-pickup-heading';
const DELIVERY_UNAVAILABLE_ERROR_TITLE_ID = 'delivery-unavailable-error-title';

/** Hide decorative SVG icons from screen readers (avoids “Graphic” before headings). */
const decorativeIconProps = {
  accessible: false,
  accessibilityElementsHidden: true,
  importantForAccessibility: 'no-hide-descendants' as const,
  'aria-hidden': true,
};

const useMqStyles = createMqStyles({
  addressContainer: {
    $base: {
      paddingVertical: 24,
      flexDirection: 'column',
      width: '100%',
    },
  },
  button: {
    $base: {
      height: 48,
      width: '100%',
    },
  },
  buttonContainer: {
    $base: {
      display: 'flex',
      paddingHorizontal: 16,
      paddingVertical: 20,
      borderColor: tokens.colors.$blackOpacity10,
      borderTopWidth: 1,
      borderStyle: 'solid',
      width: '100%',
      gap: 12,
      alignSelf: 'stretch',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
  },
  content: {
    $base: {
      display: 'flex',
      paddingVertical: 64,
      paddingHorizontal: 16,
      gap: 24,
      width: '100%',
      overflow: 'scroll',
    },
  },
  deliveryUnavailableSubcopyFocus: {
    $base: {
      alignItems: 'center',
      width: '100%',
    },
  },
  deliveryUnavailableSubcopyText: {
    $base: {
      textAlign: 'center',
    },
  },
  header: {
    $base: {
      gap: 16,
      alignItems: 'center',
      width: '100%',
    },
  },
  headerContainer: {
    $base: {
      paddingBottom: 8,
    },
  },
  page: {
    $base: {
      flex: 1,
      backgroundColor: tokens.colors.$white,
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
    },
  },
  scroll: {
    $base: { paddingBottom: 80 },
  },
  errorMessageHeader: {
    $base: { color: tokens.colors.$houseDark, fontSize: 18, fontWeight: 600, lineHeight: 25.2 },
    $gteDesktop: { fontSize: 24, lineHeight: 33.6 },
  },
  errorMessageSubHeader: {
    $base: { color: tokens.colors.$houseDark, fontSize: 14, fontWeight: 400, lineHeight: 19.6 },
    $gteDesktop: { fontSize: 16, lineHeight: 22.4 },
  },
});

type MqStyles = ReturnType<typeof useMqStyles>;

function UnavailableMessageHeader({
  styles,
  title,
  subcopy,
  webHeadingId,
}: {
  styles: MqStyles;
  title: string;
  subcopy: string;
  webHeadingId: string;
}) {
  const icon = (
    <IconDelivery
      size={80}
      color={tokens.colors.$blackOpacity10}
      {...decorativeIconProps}
    />
  );

  if (Platform.OS !== 'web') {
    return (
      <Box accessible={false} style={styles.header}>
        {icon}
        <Text accessibilityRole="header" accessible style={styles.errorMessageHeader}>
          {title}
        </Text>
        <Text accessibilityRole="text" accessible style={styles.errorMessageSubHeader}>
          {subcopy}
        </Text>
      </Box>
    );
  }

  return (
    <Box {...webNamedRegion(webHeadingId)}>
      <Box accessible={false} style={styles.header}>
        {icon}
        <Text
          nativeID={webHeadingId}
          accessibilityRole="header"
          style={styles.errorMessageHeader}
          {...webSectionHeadingWebProps(2)}
        >
          {title}
        </Text>
        <Box {...webFocusableSectionTextProps(subcopy)} style={styles.deliveryUnavailableSubcopyFocus}>
          <Text
            accessible={false}
            style={[styles.errorMessageSubHeader, styles.deliveryUnavailableSubcopyText]}
          >
            {subcopy}
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

function YourAddressSection({
  styles,
  yourAddressLabel,
  address,
  coordinates,
  webHeadingId,
}: {
  styles: MqStyles;
  yourAddressLabel: string;
  address: string;
  coordinates?: ILocation;
  webHeadingId: string;
}) {
  const addressBox = (
    <AddressBox
      icon={
        <IconLocationFill size={26} color={tokens.colors.$houseDark} {...decorativeIconProps} />
      }
      address={address}
      activeCoordinates={coordinates}
    />
  );

  if (Platform.OS !== 'web') {
    return (
      <Box accessible={false} textAlign="left" style={styles.addressContainer}>
        <Text.Heading type="four" accessibilityRole="header" accessible style={styles.headerContainer}>
          {yourAddressLabel}:
        </Text.Heading>
        {addressBox}
      </Box>
    );
  }

  return (
    <Box {...webNamedRegion(webHeadingId)} textAlign="left" style={styles.addressContainer}>
      <Text.Heading
        type="four"
        nativeID={webHeadingId}
        accessibilityRole="header"
        style={styles.headerContainer}
        {...webSectionHeadingWebProps(3)}
      >
        {yourAddressLabel}:
      </Text.Heading>
      <Box accessible={false}>{addressBox}</Box>
    </Box>
  );
}

function PickupSuggestionsSection({
  styles,
  suggestions,
  coordinates,
  webHeadingId,
}: {
  styles: MqStyles;
  suggestions: ReadonlyArray<NonNullable<React.ComponentProps<typeof AddressBox>['restaurant']>>;
  coordinates?: ILocation;
  webHeadingId: string;
}) {
  if (suggestions.length === 0) {
    return Platform.OS === 'web' ? <Box width="100%" /> : <Box accessible={false} width="100%" />;
  }

  const list = suggestions.slice(0, 2).map(suggestion => (
    <AddressBox
      key={suggestion._id}
      icon={<IconStore size={26} color={tokens.colors.$houseDark} {...decorativeIconProps} />}
      restaurant={suggestion}
      activeCoordinates={coordinates}
    />
  ));

  if (Platform.OS !== 'web') {
    return (
      <Box accessible={false} width="100%">
        <Box
          accessible={false}
          width="100%"
          textAlign="left"
          flexDirection="column"
          gap={2}
          flex={1}
        >
          <Text.Heading type="four" accessibilityRole="header" accessible style={styles.headerContainer}>
            Restaurants For Pick Up:
          </Text.Heading>
          {list}
        </Box>
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Box
        {...webNamedRegion(webHeadingId)}
        width="100%"
        textAlign="left"
        flexDirection="column"
        gap={2}
        flex={1}
      >
        <Text.Heading
          type="four"
          nativeID={webHeadingId}
          accessibilityRole="header"
          style={styles.headerContainer}
          {...webSectionHeadingWebProps(3)}
        >
          Restaurants For Pick Up:
        </Text.Heading>
        <Box flexDirection="column" gap={2} flex={1} width="100%">
          {list}
        </Box>
      </Box>
    </Box>
  );
}

const useDeliveryUnavailableAction = onChooseNewAddress => {
  const { logRBIEvent } = useCRMEventsContext();
  const onSwitchToPickup = useCallback(() => {
    logRBIEvent({
      name: CustomEventNames.DELIVERY_UNAVAILABLE_CHANGE_SERVICE_MODE,
      type: EventTypes.Navigation,
    });
    router.push('/store-locator/service-mode');
  }, [logRBIEvent]);
  const onReselectDeliveryAddress = useCallback(() => {
    logRBIEvent({
      name: '',
      type: EventTypes.Navigation,
    });
    onChooseNewAddress();
  }, [logRBIEvent, onChooseNewAddress]);
  return {
    onSwitchToPickup,
    onReselectDeliveryAddress,
  };
};

export const DeliveryUnavailableError: React.FC<
  React.PropsWithChildren<IDeliveryUnavailableErrorProps>
> = ({ onChooseNewAddress }) => {
  const { onSwitchToPickup, onReselectDeliveryAddress } =
    useDeliveryUnavailableAction(onChooseNewAddress);
  const styles = useMqStyles();
  const errorSubcopy = 'Delivery is currently unavailable';

  return (
    <Container style={styles.page} testID="delivery-unavailable-for-address-error">
      <Stack.Screen options={{ title: 'Delivery Order' }} />
      <ScrollView
        style={styles.content}
        contentContainerStyle={{ justifyContent: 'center' }}
        {...(Platform.OS === 'web' ? { accessible: false } : {})}
      >
        <UnavailableMessageHeader
          styles={styles}
          title="We're sorry!"
          subcopy={errorSubcopy}
          webHeadingId={DELIVERY_UNAVAILABLE_ERROR_TITLE_ID}
        />
      </ScrollView>
      <ButtonContainer>
        <Button onPress={onSwitchToPickup} size="xl" style={styles.button}>
          <Button.Icon>
            <IconStore {...decorativeIconProps} />
          </Button.Icon>
          <Button.Text>Pick Up Elsewhere</Button.Text>
        </Button>
        <Button
          type="outline"
          onPress={onReselectDeliveryAddress}
          testID="use-different-address"
          size="xl"
          style={styles.button}
        >
          <Button.Text>Change Delivery Address</Button.Text>
        </Button>
      </ButtonContainer>
    </Container>
  );
};

/**
 *
 * DeliveryUnavailable informs the guest that the address they entered
 * is unable to receive delivery at this time
 *
 */

export const DeliveryUnavailable: React.FC<React.PropsWithChildren<IDeliveryUnavailableProps>> = ({
  address,
  coordinates,
  onChooseNewAddress,
}) => {
  const { formatMessage } = useIntl();
  const queryCoordinates = {
    userLat: coordinates?.lat,
    userLng: coordinates?.lng,
    searchRadius: convertMilesToMeters(searchRadiusInMiles),
  };

  const { restaurants, error } = useGetNearbyRestaurantsFromDelivery(
    queryCoordinates,
    ServiceMode.DELIVERY,
    searchRadiusInMiles
  );

  const styles = useMqStyles();
  const { onSwitchToPickup, onReselectDeliveryAddress } =
    useDeliveryUnavailableAction(onChooseNewAddress);

  const suggestions = restaurants || [];

  const subtext =
    suggestions.length > 0
      ? 'No available delivery restaurants found within 10 miles of your address.'
      : 'No stores found within 20 miles of your address';

  if (error) {
    return <DeliveryUnavailableError onChooseNewAddress={onChooseNewAddress} />;
  }

  const yourAddressLabel = formatMessage({ id: 'yourAddress' });

  return (
    <Container style={styles.page} testID="delivery-unavailable-for-address">
      <ScrollView
        style={styles.content}
        bounces={false}
        contentContainerStyle={styles.scroll}
        {...(Platform.OS === 'web' ? { accessible: false } : {})}
      >
        <UnavailableMessageHeader
          styles={styles}
          title="Sorry, No Delivery Restaurants Nearby"
          subcopy={subtext}
          webHeadingId={DELIVERY_UNAVAILABLE_TITLE_ID}
        />
        <YourAddressSection
          styles={styles}
          yourAddressLabel={yourAddressLabel}
          address={address}
          coordinates={coordinates}
          webHeadingId={DELIVERY_UNAVAILABLE_YOUR_ADDRESS_HEADING_ID}
        />
        <PickupSuggestionsSection
          styles={styles}
          suggestions={suggestions}
          coordinates={coordinates}
          webHeadingId={DELIVERY_UNAVAILABLE_PICKUP_HEADING_ID}
        />
      </ScrollView>

      <Box style={styles.buttonContainer}>
        <Button onPress={onSwitchToPickup} size="xl" style={styles.button}>
          <Button.Icon>
            <IconStore {...decorativeIconProps} />
          </Button.Icon>
          <Button.Text>Pick Up Elsewhere</Button.Text>
        </Button>
        <Button
          type="outline"
          onPress={onReselectDeliveryAddress}
          testID="use-different-address"
          size="xl"
          style={styles.button}
        >
          <Button.Text>Change Delivery Address</Button.Text>
        </Button>
      </Box>
    </Container>
  );
};
